/**
 * Carriers — a hostile capital CARRIER (its own big dark hull, evil red glow).
 *
 * A rarer, tougher cousin of the Cruiser: appears only from the 6th drive jump on,
 * never more than 1 per planet layer, and is slow and heavily armored.
 *
 * Fighting style:
 *   • Aggro → hold a long standoff and orbit the player, tracking with 8 free-rotating
 *     turrets (same slot count as the player's Mk III) that fire twin red bolts.
 *   • Its signature — the HANGAR: it launches a wing of 6 fighters from its two side
 *     bays (2 at a time, one per bay). Those 6 are ONE-SHOT: once destroyed they never
 *     respawn (handled by the EnemyManager via the `noRespawn` flag).
 *   • Like the Cruiser, it can call ONE jump-in reinforcement squad per life.
 *
 * A Carrier satisfies the turret/cannon `Damageable` shape, so the player's turrets,
 * cannons, and lock-on target it directly.
 */
import { Vector2 } from "@/math/Vector2";
import { ENEMY_CARRIER } from "@/config/balance";
import type { MineableBody } from "@/game/world/body";
import type { EnemyBullet } from "./EnemyShips";

export interface CarrierTurret {
  angle: number; // world aim angle
  cooldown: number;
  flash: number; // muzzle flash 0..1
}

export interface Carrier {
  pos: Vector2;
  vel: Vector2;
  heading: number;
  hp: number;
  maxHp: number;
  shield: number;
  maxShield: number;
  shieldFlash: number;
  hitTimer: number;
  turrets: CarrierTurret[]; // always length ENEMY_CARRIER.mounts.length (8)
  aggro: boolean;
  broadside: 1 | -1; // which way it circles the player
  roam: Vector2;
  dead: boolean; // death handled (XP/explosion awarded once)
  backupState: number; // 0 = unused, 1 = broadcasting radio, 2 = spent
  backupTimer: number; // radio-broadcast countdown (also drives the VFX phase)
  fightersLaunched: number; // how many of its 6 hangar fighters are away
  launchTimer: number; // countdown to the next 2-fighter launch batch
}

export interface CarrierCtx {
  playerX: number;
  playerY: number;
  playerRadius: number;
  playerAlive: boolean;
  playerSpeed: number; // the player's current max speed (cruise scales off this)
  warp: number; // drive-jump count — call-for-backup unlocks from backupFromWarp
  bodies: MineableBody[];
  onHitPlayer: (dmg: number, x: number, y: number) => void;
  onDeath: (x: number, y: number) => void;
  onCallBackup: (x: number, y: number) => void; // summon the jump-in squad
  onLaunchFighter: (x: number, y: number, heading: number) => void; // eject a hangar fighter
}

function norm(a: number): number {
  while (a > Math.PI) a -= Math.PI * 2;
  while (a < -Math.PI) a += Math.PI * 2;
  return a;
}
function clamp(v: number, lo: number, hi: number): number {
  return v < lo ? lo : v > hi ? hi : v;
}
function randomWaypoint(): Vector2 {
  const a = Math.random() * Math.PI * 2;
  const d = 300 + Math.random() * 1400;
  return new Vector2(Math.cos(a) * d, Math.sin(a) * d);
}

export class CarrierManager {
  readonly ships: Carrier[] = [];
  readonly bullets: EnemyBullet[] = [];
  private readonly spawn: { x: number; y: number };

  constructor(count: number, spawn: { x: number; y: number } = { x: 0, y: 0 }) {
    this.spawn = { x: spawn.x, y: spawn.y };
    for (let i = 0; i < count; i++) this.ships.push(this.make());
  }

  private spawnPos(): Vector2 {
    for (let tries = 0; tries < 12; tries++) {
      const a = Math.random() * Math.PI * 2;
      const d = 1000 + Math.random() * 700;
      const x = Math.cos(a) * d;
      const y = Math.sin(a) * d;
      const dx = x - this.spawn.x;
      const dy = y - this.spawn.y;
      if (dx * dx + dy * dy >= ENEMY_CARRIER.minSpawnDist * ENEMY_CARRIER.minSpawnDist) {
        return new Vector2(x, y);
      }
    }
    return new Vector2(-this.spawn.x, -this.spawn.y - 1200);
  }

  private make(): Carrier {
    const pos = this.spawnPos();
    const heading = Math.atan2(-pos.y, -pos.x);
    const turrets: CarrierTurret[] = [];
    for (let i = 0; i < ENEMY_CARRIER.mounts.length; i++) {
      turrets.push({
        angle: heading,
        cooldown: Math.random() * ENEMY_CARRIER.fireInterval,
        flash: 0,
      });
    }
    return {
      pos,
      vel: new Vector2(),
      heading,
      hp: ENEMY_CARRIER.maxHp,
      maxHp: ENEMY_CARRIER.maxHp,
      shield: ENEMY_CARRIER.maxShield,
      maxShield: ENEMY_CARRIER.maxShield,
      shieldFlash: 0,
      hitTimer: 999,
      turrets,
      aggro: false,
      broadside: Math.random() < 0.5 ? 1 : -1,
      roam: randomWaypoint(),
      dead: false,
      backupState: 0,
      backupTimer: 0,
      fightersLaunched: 0,
      launchTimer: 0,
    };
  }

  update(dt: number, ctx: CarrierCtx): void {
    for (const s of this.ships) this.updateShip(s, dt, ctx);

    // Advance red bolts; a strike on the player deals damage.
    for (let i = this.bullets.length - 1; i >= 0; i--) {
      const b = this.bullets[i]!;
      b.x += b.vx * dt;
      b.y += b.vy * dt;
      b.life -= dt;
      let hit = false;
      if (ctx.playerAlive) {
        const rr = ctx.playerRadius + 5;
        if ((ctx.playerX - b.x) ** 2 + (ctx.playerY - b.y) ** 2 < rr * rr) {
          ctx.onHitPlayer(ENEMY_CARRIER.boltDamage, b.x, b.y);
          hit = true;
        }
      }
      if (hit || b.life <= 0) this.bullets.splice(i, 1);
    }
  }

  private updateShip(s: Carrier, dt: number, ctx: CarrierCtx): void {
    if (s.hp <= 0) {
      if (!s.dead) {
        ctx.onDeath(s.pos.x, s.pos.y);
        s.dead = true;
      }
      return; // capital ships do not respawn
    }

    s.shieldFlash = Math.max(0, s.shieldFlash - dt * 3);
    s.hitTimer += dt;
    for (const t of s.turrets) {
      t.flash = Math.max(0, t.flash - dt * 5);
      t.cooldown -= dt;
    }

    const dx = ctx.playerX - s.pos.x;
    const dy = ctx.playerY - s.pos.y;
    const dist = Math.hypot(dx, dy) || 1e-3;
    const toPlayer = Math.atan2(dy, dx);
    if (dist < ENEMY_CARRIER.aggroRange) s.aggro = true;
    else if (dist > ENEMY_CARRIER.aggroRange * 1.6) s.aggro = false;

    // Call for backup — ONCE per life, from warp 6, once it's taken some damage.
    if (
      s.backupState === 0 &&
      s.aggro &&
      ctx.playerAlive &&
      ctx.warp >= ENEMY_CARRIER.backupFromWarp &&
      s.hp < s.maxHp * 0.85
    ) {
      s.backupState = 1;
      s.backupTimer = ENEMY_CARRIER.backupRadioTime;
    }
    if (s.backupState === 1) {
      s.backupTimer -= dt;
      if (s.backupTimer <= 0) {
        ctx.onCallBackup(s.pos.x, s.pos.y);
        s.backupState = 2;
      }
    }

    // Hangar: once engaged, stream its 6 fighters out — 2 at a time (one per bay),
    // launchInterval apart — until the whole wing is away. They never respawn.
    if (s.aggro && ctx.playerAlive && s.fightersLaunched < ENEMY_CARRIER.fighters) {
      s.launchTimer -= dt;
      if (s.launchTimer <= 0) {
        const fx = Math.cos(s.heading);
        const fy = Math.sin(s.heading);
        const px = -fy;
        const py = fx;
        for (const gate of ENEMY_CARRIER.hangarGates) {
          if (s.fightersLaunched >= ENEMY_CARRIER.fighters) break;
          const mx = s.pos.x + fx * gate.fwd + px * gate.lat;
          const my = s.pos.y + fy * gate.fwd + py * gate.lat;
          ctx.onLaunchFighter(mx, my, s.heading);
          s.fightersLaunched++;
        }
        s.launchTimer = ENEMY_CARRIER.launchInterval;
      }
    }

    let desired: number;
    let speed: number;

    if (s.aggro && ctx.playerAlive) {
      // Orbit the player at the standoff distance, circling one way.
      const cruise = clamp(
        ctx.playerSpeed * ENEMY_CARRIER.speedMul,
        ENEMY_CARRIER.speedMin,
        ENEMY_CARRIER.speedMax,
      );
      const tangent = toPlayer + s.broadside * (Math.PI / 2);
      const radialErr = dist - ENEMY_CARRIER.standoff; // >0 too far, <0 too close
      const pull = clamp(radialErr / 320, -1, 1);
      const rx = dx / dist;
      const ry = dy / dist;
      const vx = Math.cos(tangent) + rx * pull * 0.9;
      const vy = Math.sin(tangent) + ry * pull * 0.9;
      desired = Math.atan2(vy, vx);
      speed = cruise;
    } else {
      if ((s.roam.x - s.pos.x) ** 2 + (s.roam.y - s.pos.y) ** 2 < 120 * 120) {
        s.roam = randomWaypoint();
      }
      desired = Math.atan2(s.roam.y - s.pos.y, s.roam.x - s.pos.x);
      speed = ENEMY_CARRIER.patrolSpeed;
    }

    desired = this.avoidRocks(s, ctx.bodies, desired);
    this.steer(s, dt, desired, speed);
    this.updateTurrets(s, dt, ctx, dist);

    if (s.hitTimer >= ENEMY_CARRIER.regenDelay && s.shield < s.maxShield) {
      s.shield = Math.min(s.maxShield, s.shield + ENEMY_CARRIER.shieldRegenPerSec * dt);
    }
  }

  /** Like the player's/cruiser's turrets: each mount only covers its own side (a flank
   * arc around its outward beam) and holds fire if the player isn't on that side, so at
   * most ~4 of the 8 bear on the player at once. */
  private updateTurrets(s: Carrier, dt: number, ctx: CarrierCtx, dist: number): void {
    const h = s.heading;
    // The carrier hull renders FLIPPED (rotation = heading + π/2) so it flies nose-first;
    // the mount transform + flank side use that so turrets sit on the painted gun ports.
    const ct = -Math.sin(h); // cos(h + π/2)
    const st = Math.cos(h); // sin(h + π/2)
    const hs = ENEMY_CARRIER.spriteWorldHeight / ENEMY_CARRIER.spriteTexHeight;
    const canFire = s.aggro && ctx.playerAlive && dist < ENEMY_CARRIER.turretRange;
    for (let i = 0; i < s.turrets.length; i++) {
      const t = s.turrets[i]!;
      const off = ENEMY_CARRIER.mounts[i]!;
      const mx = s.pos.x + hs * (off.ox * ct - off.oy * st);
      const my = s.pos.y + hs * (off.ox * st + off.oy * ct);
      const aim = Math.atan2(ctx.playerY - my, ctx.playerX - mx);
      // Flipped hull → texture-left ports sit on the ship's right, so invert the arc side.
      const arcSign = off.ox < 0 ? -1 : 1;
      const onSide = arcSign * norm(aim - h) >= -0.03;
      const d = norm(aim - t.angle);
      const step = ENEMY_CARRIER.turretTurn * dt;
      t.angle += d > step ? step : d < -step ? -step : d;
      if (
        canFire &&
        onSide &&
        t.cooldown <= 0 &&
        Math.abs(norm(aim - t.angle)) < ENEMY_CARRIER.fireCone
      ) {
        const ax = Math.cos(t.angle);
        const ay = Math.sin(t.angle);
        const bx = -ay; // perpendicular → twin bolts fly parallel
        const by = ax;
        for (let k = 0; k < ENEMY_CARRIER.boltsPerShot; k++) {
          const off2 = (k * 2 - (ENEMY_CARRIER.boltsPerShot - 1)) * ENEMY_CARRIER.barrelSpread;
          this.bullets.push({
            x: mx + ax * ENEMY_CARRIER.muzzleLen + bx * off2,
            y: my + ay * ENEMY_CARRIER.muzzleLen + by * off2,
            vx: ax * ENEMY_CARRIER.boltSpeed,
            vy: ay * ENEMY_CARRIER.boltSpeed,
            life: ENEMY_CARRIER.boltLife,
          });
        }
        t.cooldown = ENEMY_CARRIER.fireInterval;
        t.flash = 1;
      }
    }
  }

  private avoidRocks(s: Carrier, bodies: MineableBody[], desired: number): number {
    let ax = 0;
    let ay = 0;
    for (const b of bodies) {
      if (b.ore <= 0) continue;
      const dx = s.pos.x - b.pos.x;
      const dy = s.pos.y - b.pos.y;
      const rr = b.radius + ENEMY_CARRIER.collisionRadius + ENEMY_CARRIER.avoidRange;
      const d2 = dx * dx + dy * dy;
      if (d2 < rr * rr && d2 > 1) {
        const d = Math.sqrt(d2);
        const w = (rr - d) / rr;
        ax += (dx / d) * w;
        ay += (dy / d) * w;
      }
    }
    if (ax === 0 && ay === 0) return desired;
    const dvx = Math.cos(desired) + ax * 1.8;
    const dvy = Math.sin(desired) + ay * 1.8;
    return Math.atan2(dvy, dvx);
  }

  private steer(s: Carrier, dt: number, desired: number, speed: number): void {
    const diff = norm(desired - s.heading);
    const maxTurn = ENEMY_CARRIER.turnRate * dt;
    s.heading += diff > maxTurn ? maxTurn : diff < -maxTurn ? -maxTurn : diff;
    const vx = Math.cos(s.heading) * speed;
    const vy = Math.sin(s.heading) * speed;
    s.vel.set(vx, vy);
    s.pos.x += vx * dt;
    s.pos.y += vy * dt;
  }
}
